import React from 'react';
import { Box, Typography } from '@mui/material';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { formatMonedaCompacta } from '../state/format';
import { calcularSerieMensual } from '../state/metrics';
import { BolsaCentro } from '../types';

interface TrendChartProps {
  cotizaciones: BolsaCentro[];
}

export const TrendChart: React.FC<TrendChartProps> = ({ cotizaciones }) => {
  const serie = calcularSerieMensual(cotizaciones, 6);
  const width = 720;
  const height = 220;
  const marginL = 40;
  const marginR = 16;
  const marginT = 18;
  const marginB = 36;
  const innerW = width - marginL - marginR;
  const innerH = height - marginT - marginB;

  const maxValor = Math.max(...serie.map((p) => Math.max(p.cotizado, p.ganado)), 1);

  const x = (i: number) => marginL + (i / Math.max(serie.length - 1, 1)) * innerW;
  const y = (v: number) => marginT + innerH - (v / maxValor) * innerH;

  const puntosCotizado = serie.map((p, i) => `${x(i)},${y(p.cotizado)}`).join(' ');
  const puntosGanado = serie.map((p, i) => `${x(i)},${y(p.ganado)}`).join(' ');
  const areaCotizado = `${marginL},${marginT + innerH} ${puntosCotizado} ${x(serie.length - 1)},${marginT + innerH}`;

  return (
    <Box data-testid="trend-chart">
      {/* SPEC-105 (CH-01) — título y wrapper .chart-card (fondo/radio/sombra) ahora los
          provee el SectionCard que envuelve este componente en ResumenTab; se elimina
          el duplicado para no anidar dos tarjetas. */}
      <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: '100%', height: 'auto' }}>
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={marginL}
            x2={width - marginR}
            y1={marginT + innerH * (1 - f)}
            y2={marginT + innerH * (1 - f)}
            stroke={TOKENS.hair}
            strokeWidth={1}
          />
        ))}
        {[0, 0.5, 1].map((f) => (
          <text key={f} x={4} y={marginT + innerH * (1 - f) + 4} style={{ fontSize: fs(10.5) }} fill={TOKENS.muted} fontFamily="Poppins, sans-serif">
            {formatMonedaCompacta(maxValor * f)}
          </text>
        ))}

        <polygon points={areaCotizado} fill={TOKENS.wash} />
        <polyline points={puntosCotizado} fill="none" stroke={TOKENS.faint} strokeWidth={1.5} strokeDasharray="4 4" />
        <polyline points={puntosGanado} fill="none" stroke={TOKENS.purple} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />

        {serie.map((p, i) => (
          <g key={p.mes}>
            <circle cx={x(i)} cy={y(p.ganado)} r={4} fill={TOKENS.bg} stroke={TOKENS.purple} strokeWidth={2.5} />
            <text x={x(i)} y={y(p.ganado) - 10} style={{ fontSize: fs(10.5) }} fontWeight={600} fill={TOKENS.purple} textAnchor="middle" fontFamily="Poppins, sans-serif">
              {formatMonedaCompacta(p.ganado)}
            </text>
            <text x={x(i)} y={height - 12} style={{ fontSize: fs(11) }} fill={TOKENS.muted} textAnchor="middle" fontFamily="Poppins, sans-serif">
              {p.mes}
            </text>
          </g>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: TOKENS.wash, border: `1px dashed ${TOKENS.faint}` }} />
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>Cotizado</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '2px', backgroundColor: TOKENS.purple }} />
          <Typography sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(11), color: TOKENS.muted }}>Ganado</Typography>
        </Box>
      </Box>
    </Box>
  );
};
